/**
 * ADS-FIX-2026-003 — turn a `stock_unavailable` refusal from a dead end into a path forward.
 *
 * WHY THIS EXISTS
 * ---------------
 * `stock_unavailable` was half of every `sale_checkout_blocked` in 2026-09-28…10-04 (45 events, 11
 * users), and 0 of those 11 users reached `sale_created` afterwards. The refusal said only «Товара X
 * нет в наличии» — in a 3-second toast at the cart stage, or a red line at checkout — so a cashier
 * selling 5 against 2 in stock was told the product was absent, was not told how many were there,
 * and was not shown how to put stock in. They tapped again, and again.
 *
 * The refusal ITSELF is unchanged and must stay so: Atlas is accounting software, selling past
 * stock is a role permission (`canSellOutOfStock`), and nothing here weakens it. What this adds is
 * the facts (which product, how many are available, how many were attempted) and one explicit way
 * forward: the ordinary acceptance (receiving) form, prefilled with the short products.
 *
 * NOTHING IS WRITTEN FROM HERE. `receivingParams` produces navigation params for `SupplyDetail`,
 * whose own Save is the only thing that creates a supply or moves stock — exactly as when the same
 * form is opened from a product's stock screen or from an agent draft. Closing that form without
 * saving changes nothing.
 */

import { isPseudoLine } from '../global/Firebase/cartLineType'

/** One product the cart could not draw from, described for the cashier. */
export interface StockShortage {
  /** The product whose stock is short — for an at-sale composite, the INGREDIENT. */
  productId: string | null
  productName: string | null
  /** For an ingredient shortage, the composite being sold; otherwise null. */
  ingredientOf: string | null
  /** The live stock in this store as the guard compared it, or null when none is recorded. */
  available: number | null
  /** What the cashier tried to take from that product. */
  attempted: number
}

type ProductLike = Record<string, unknown> & { id?: string | null, name?: string | null }
export type ShortageProductsDict = Record<string, ProductLike | undefined> | null | undefined

/** A finite number, or null. Stock values reach the client as numbers, numeric strings or null. */
const finiteOrNull = (raw: unknown): number | null => {
  if (raw === undefined || raw === null || raw === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * A product's stock in `store` exactly as the refusal compared it, or null when there is no answer.
 *
 * The cart-stage guard is the loose `storeStock?.[store] <= 0`, which REFUSES a recorded `null` or
 * `''` (both coerce to 0) and lets `undefined` through. So the number shown for a refused product
 * must be the one it was refused on — 0 for `null`/`''` — the same semantics as the checkout
 * guard's `comparableNumber` (utils/outOfStockGuard.ts). Showing «—» there would tell the cashier
 * the stock is unknown when the app has just treated it as zero.
 */
export const productStoreStock = (
  product: ProductLike | null | undefined,
  store: string,
): number | null => {
  const stock = product?.storeStock as Record<string, unknown> | null | undefined
  const raw = stock?.[store]
  if (raw === undefined) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * A quantity as the cashier should read and type it: products sold by weight multiply into float
 * noise (0.1 × 3 = 0.30000000000000004), which is not a quantity anyone attempted.
 */
export const roundQuantity = (value: number): number => Math.round(value * 1000) / 1000

/**
 * Add a shortage to the list on screen. One row per product: a later refusal of the same product
 * replaces the earlier one, because its numbers are the current ones. Rows without an id are kept
 * as they come — they cannot be deduplicated, and dropping one would hide a refusal.
 */
export const addStockShortage = (
  current: readonly StockShortage[],
  next: StockShortage,
): StockShortage[] => {
  if (!next.productId) return [...current, next]
  return [...current.filter((s) => s.productId !== next.productId), next]
}

interface DrawCartLine {
  id?: string | null
  quantity?: unknown
  isComposite?: boolean | null
  manufacturedBeforeSale?: boolean | null
  ingredients?: Array<{ id?: string | null, quantity?: unknown }> | null
}

/**
 * What the cart ALREADY draws from `productId`, with the same line semantics as the checkout guard
 * (`findInsufficientStockLine`) and the write path: pseudo lines and lines with no positive quantity
 * draw nothing, an at-sale composite draws `quantity × recipe amount` of each ingredient, anything
 * else draws its own quantity.
 *
 * A cart-stage refusal happens BEFORE the line enters the cart, so «what you are trying to sell» is
 * this plus the line being added — otherwise a cart already holding 4 of a product reports 1, and
 * an acceptance of 1 leaves checkout refusing the 5.
 */
export const cartDrawOnProduct = (
  cartItems: readonly DrawCartLine[] | null | undefined,
  productId: string | null | undefined,
): number => {
  if (!productId) return 0
  let total = 0
  for (const line of cartItems ?? []) {
    if (isPseudoLine(line)) continue
    const quantity = Number(line?.quantity)
    if (!(quantity > 0)) continue
    if (line.isComposite && !line.manufacturedBeforeSale) {
      for (const ingredient of line.ingredients ?? []) {
        if (ingredient?.id !== productId) continue
        // The guard skips an uncomparable draw rather than letting it poison the running total
        // (`exceedsStock` in outOfStockGuard.ts); one unreadable recipe amount must not turn the
        // whole figure into «—» here either.
        const draw = quantity * Number(ingredient.quantity)
        if (Number.isFinite(draw)) total += draw
      }
    } else if (line.id === productId) {
      total += quantity
    }
  }
  return total
}

export interface ReceivingParams {
  preselectedItems: Array<Record<string, unknown>>
  preselectedStore: string
}

/**
 * Navigation params for the standard acceptance form (`SupplyDetail`), prefilled with ONE short
 * product at the quantity that was attempted — or null when that product can no longer be received.
 *
 * One product per acceptance, never the whole list: the form takes its supplier from the first
 * prefilled item, so two short products from different suppliers in one prefill would book one
 * supplier's goods and cost against the other's payable. One product carries its own supplier,
 * exactly like «Создать приёмку» on the product stock screen.
 *
 * The product object comes from the LIVE catalog (the shape `ProductStockMovementScreen` passes),
 * so the form shows current prices and supplier. A product no longer in the catalog cannot be
 * received and yields null; the caller then offers no button for it rather than an empty form.
 *
 * The attempted quantity is a starting value only — the form's quantity field is editable and
 * nothing is saved until the cashier confirms there.
 */
export const receivingParams = (
  shortage: StockShortage,
  productsDict: ShortageProductsDict,
  store: string,
): ReceivingParams | null => {
  const id = shortage.productId
  if (!id) return null
  const product = productsDict?.[id]
  if (!product) return null
  const attempted = finiteOrNull(shortage.attempted)
  return {
    preselectedItems: [{ ...product, id, quantity: attempted !== null && attempted > 0 ? roundQuantity(attempted) : 0 }],
    preselectedStore: store,
  }
}

/** The acceptance form `receivingParams` opens — the route a stock-shortage refusal pushes. */
export const RECEIVING_ROUTE = 'SupplyDetail'

interface NavigationStateLike {
  index?: number
  routes?: ReadonlyArray<{ name?: string } | undefined>
}

/**
 * Whether the acceptance form is the screen on top of New Sale's stack.
 *
 * New Sale listens for barcode-scanner keystrokes on the whole web document, and the acceptance
 * form is pushed ON TOP of New Sale (which stays mounted so the cart survives). Without this
 * check a scan made on the acceptance form — the natural thing to do while receiving goods —
 * would be swallowed by New Sale: added to the hidden sale cart, or refused there and reported
 * as a `stock_unavailable` the cashier never attempted. Only the screen this change opens is
 * excluded; what the listener does under every other route is left exactly as it was.
 */
export const isReceivingFormOnTop = (state: NavigationStateLike | null | undefined): boolean => {
  const index = state?.index
  if (typeof index !== 'number') return false
  return state?.routes?.[index]?.name === RECEIVING_ROUTE
}

